import React, { useState } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Tooltip,
  ListItemAvatar,
  ListItemText,
  Popper,
  Paper,
  ClickAwayListener,
} from "@mui/material";
import CustomDropdown from "../Common/CustomDropdown";
import AddPersonIcon from "../../assets/interaction-detail-page/Add_person_icon.svg";
import "../../styles/interactionDetailsPage.css";
import { getAvatarColor, getAvatarInitials } from "../../utils/utils";

type Subscriber = {
  type: "user" | "space";
  name?: string;
  email?: string;
  id?: string;
  pictureUrl?: string;
  picture?: string;
  profileOrgRelationshipId?: string;
};

type Props = {
  selectedSubscribers: Subscriber[];
  attendeeParticipants: Subscriber[];
  canEdit: boolean;
  row: any;
  rowAction: {
    updateSubscribersCallBack: (
      row: any,
      value: { actionSubscribers: string[]; deleteSubscribers: string[]; addSpaces?: string[]; deleteSpaces?: string[]; }
    ) => void;
  };
  noBorder?: boolean;
  spacesList?: any[];
  onOpen?: () => void;
  onClose?: () => void;
  disableTooltip?: boolean;
};

const ActionItemSubscribersCell: React.FC<Props> = ({
  selectedSubscribers = [],
  attendeeParticipants = [],
  canEdit,
  row,
  rowAction,
  noBorder,
  spacesList = [],
  onOpen,
  onClose,
  disableTooltip,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tempSelected, setTempSelected] = useState<Subscriber[]>(selectedSubscribers || []);
  const spaceOptions: Subscriber[] = spacesList.map((space) => ({
    type: "space",
    name: space.spaceName,
    id: space.spaceId,
  }));

  const userOptions: Subscriber[] = attendeeParticipants.map((u) => ({
    type: "user",
    name: u.name,
    email: u.email,
    pictureUrl: u.pictureUrl,
    picture: u.picture,
    profileOrgRelationshipId: u.profileOrgRelationshipId,
  }));

  const allOptions = [...userOptions, ...spaceOptions];

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (canEdit) {
      setTempSelected(selectedSubscribers || []); // reset temp selection on open
      setAnchorEl(event.currentTarget);
      onOpen?.();
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
    onClose?.();
  };
  const hasMultipleSubscribers = selectedSubscribers.length > 1;
  const disableAvatarTooltip =
  Boolean(disableTooltip) || hasMultipleSubscribers;

  const handleUpdateSubscribers = (users: Subscriber[]) => {
    const prevUsers = selectedSubscribers.filter(s => s.type === "user");
    const prevSpaces = selectedSubscribers.filter(s => s.type === "space");

    const nextUsers = users.filter(s => s.type === "user");
    const nextSpaces = users.filter(s => s.type === "space");

    // USER DIFF
    const prevEmails = new Set(prevUsers.map(u => u.profileOrgRelationshipId));
    const nextEmails = new Set(nextUsers.map(u => u.profileOrgRelationshipId));

    const actionSubscribers = [...nextEmails].filter(profileOrgRelationshipId => !prevEmails.has(profileOrgRelationshipId));
    const deleteSubscribers = [...prevEmails].filter(profileOrgRelationshipId => !nextEmails.has(profileOrgRelationshipId ));

    // SPACE DIFF
    const prevSpaceIds = new Set(prevSpaces.map(s => s.id));
    const nextSpaceIds = new Set(nextSpaces.map(s => s.id));

    const addSpaces = [...nextSpaceIds].filter(id => !prevSpaceIds.has(id));
    const deleteSpaces = [...prevSpaceIds].filter(id => !nextSpaceIds.has(id));
    if (
      actionSubscribers.length === 0 &&
      deleteSubscribers.length === 0 &&
      addSpaces.length === 0 &&
      deleteSpaces.length === 0
    ) {
      handleClose();
      return;
    }
    setTempSelected(users);
    const payload = {
      actionSubscribers: actionSubscribers.filter(Boolean) as string[],
      deleteSubscribers: deleteSubscribers.filter(Boolean) as string[],
      addSpaces: addSpaces.filter(Boolean) as string[],
      deleteSpaces: deleteSpaces.filter(Boolean) as string[],
    };

    rowAction.updateSubscribersCallBack(row, payload);

    handleClose();
  };

  const displaySubscriber = selectedSubscribers?.[0];
  const extraCount =
    selectedSubscribers.length > 1 ? selectedSubscribers.length - 1 : 0;

  return (
    <Box
      className={
        noBorder
          ? ""
          : `assignee-cell-wrapper ${
              open ? "assignee-cell-wrapper--active" : ""
            } ${!canEdit ? "no-hover" : ""}`
      }
      onClick={handleOpen}
      sx={{ display: "flex", alignItems: "center", paddingRight: "70px" }}
    >
      {selectedSubscribers.length > 0 ? (
        <>
          <Tooltip
            title={displaySubscriber?.name || displaySubscriber?.email}
            placement="top"
            arrow
            disableHoverListener={disableAvatarTooltip}
          >
            <IconButton>
            <Avatar
              src={displaySubscriber?.picture || displaySubscriber?.pictureUrl || ""}
              alt={displaySubscriber?.name || displaySubscriber?.email}
              variant={displaySubscriber?.type === "space" ? "square" : "circular"}
              sx={{
                bgcolor:
                  !displaySubscriber?.picture && !displaySubscriber?.pictureUrl
                    ? getAvatarColor(displaySubscriber.email || displaySubscriber.name || "")
                    : undefined,
                width: displaySubscriber?.type === "space" ?  20 : 22,
                height: displaySubscriber?.type === "space" ? 20 : 22,
                fontSize: 10,
                borderRadius: displaySubscriber?.type === "space" ? "4px" : "",
              }}
            >
              {!displaySubscriber?.picture && !displaySubscriber?.pictureUrl &&
                getAvatarInitials(displaySubscriber.email || displaySubscriber.name || "")}
            </Avatar>
            </IconButton>
          </Tooltip>
          {extraCount > 0 && (
            <Box sx={{ fontSize: 12, color: "#000" }}>+{extraCount}</Box>
          )}
        </>
      ) : canEdit ? (
        <Tooltip title="Add Subscribers" placement="top" arrow>
          <IconButton>
            <img src={AddPersonIcon} alt="Add Person" />
          </IconButton>
        </Tooltip>
      ) : (
        <Box sx={{ width: "100%", paddingLeft: "10px", fontSize: 13, color: "#555" }}>-</Box>
      )}

      {canEdit && open && (
        <ClickAwayListener onClickAway={handleClose}>
        <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom"
        modifiers={[
          {
            name: "flip",
            enabled: true,
            options: {
              fallbackPlacements: ["top"],
            },
          },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              boundary: "viewport",
              padding: 8,
            },
          },
        ]}
        sx={{ zIndex: 1300 }}
      >
        <Paper
          style={{
            maxHeight: 260,
            minWidth: 250,
            overflow: "auto",
            scrollbarWidth: "thin",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <CustomDropdown<Subscriber>
            items={allOptions}
            value={tempSelected}
            groupBy={(item) => item.type === "space" ? "Spaces" : "People"}
            onChange={(users) => handleUpdateSubscribers(users as Subscriber[])}
            placeholder="Search by name"
            maxHeight={260}
            showSearch
            multiple
            getKey={(item) =>
              item.type === "user"
                ? item.profileOrgRelationshipId || ""
                : item.id || ""
            }
            isSubscriber
            filterFn={(p, search) =>
              (p.name || "").toLowerCase().includes(search.toLowerCase())
            }
            renderItem={(item) => (
              <div className="flex items-center" key={item.id || item.profileOrgRelationshipId}>
                <ListItemAvatar sx={{ minWidth: "28px" }}>
                  {item.type === "space" ? (
                    <Avatar
                      variant="square"
                      sx={{
                        bgcolor: getAvatarColor(item.name),
                        width: 20,
                        height: 20,
                        fontSize: 10,
                        color: "#ffff",
                        borderRadius: "4px",
                      }}
                    >
                      {getAvatarInitials(item.name)}
                    </Avatar>
                  ) : (
                    <Avatar
                      src={item.picture || item.pictureUrl || ""}
                      alt={item.name}
                      sx={{
                        bgcolor:
                          !item.picture && !item.pictureUrl
                            ? getAvatarColor(item.email || item.name)
                            : undefined,
                        width: 22,
                        height: 22,
                        fontSize: 10,
                      }}
                    >
                      {!item.picture && !item.pictureUrl &&
                        getAvatarInitials(item.email || item.name)}
                    </Avatar>
                  )}
                </ListItemAvatar>
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{ noWrap: true }}
                />
              </div>
            )}
          />
        </Paper>
      </Popper>
      </ClickAwayListener>
      )}
    </Box>
  );
};

export default ActionItemSubscribersCell;
